import { computed, makeObservable } from 'mobx';

import {
  AIAggregatedDocType,
  AIDocsServer,
  AIBotServer,
  IAIBot,
  mapAIBotGetErrorToTitle,
  AIDocServer,
  normalizeAIBotChannelIds,
  normalizeAIBotScenarioData,
  IAIBotScenarioData,
  AIModelKind
} from 'shared/entities/smartbotAI';
import { IRootStore } from 'shared/entities/store/rootStore';
import { apiUrls } from 'shared/entities/domain';
import { getDomainNameFromUrl } from 'shared/entities/common/utils';
import { AnalyticsEvent } from 'shared/entities/analytics';
import { AITarget } from 'shared/entities/smartbotAIInfo';

import { OpenStateModel } from '../openState';
import NewListModel from '../NewListModel';
import { FieldModel } from '../form';
import { AITarifficationModel } from '../tariffication';

import { AIAggregatedPdfDocModel } from './AIAggregatedPdfDocModel';
import { AIAggregatedSiteDocModel } from './AIAggregatedSiteDocModel';
import { AIAggregatedDocModel } from './AIAggregatedDocModel';
import { AIEducationParamsModel } from './AIEducationParamsModel';
import { AIBotModelBase } from './AIBotModelBase';

/** Опрос прогресса индексации агрегированных документов. Раньше 10s — UI «замирал» между значениями с бэка. */
const DOCS_STATUS_POLL_INTERVAL_MS = 3000;
export const TEXTS_MAX_LENGTH = 5500;

export class AIBotModel extends AIBotModelBase implements IAIBot {
  readonly pdfDocs: NewListModel<AIAggregatedPdfDocModel> =
    new NewListModel<AIAggregatedPdfDocModel>();
  readonly siteDocs: NewListModel<AIAggregatedSiteDocModel> =
    new NewListModel<AIAggregatedSiteDocModel>();

  readonly siteIndexingModalState = new OpenStateModel();

  readonly tariffication: AITarifficationModel;

  private timerId: ReturnType<typeof setTimeout> | null = null;

  private aiModelKind: FieldModel<AIModelKind>;
  private target: FieldModel<AITarget>;

  constructor({
    id,
    params,
    scenarios,
    channelIds,
    tariffication,
    rootStore
  }: {
    id: string;
    params: AIEducationParamsModel;
    scenarios: IAIBotScenarioData[];
    channelIds: string[] | null;
    tariffication: AITarifficationModel;
    rootStore: IRootStore;
  }) {
    super({ id, params, scenarios, channelIds, rootStore });

    this.aiModelKind = new FieldModel(params.aiModelKind.value);
    this.target = new FieldModel(params.target.value);
    this.tariffication = tariffication;

    makeObservable(this, {
      intentTargetTitle: computed,
      allDocs: computed,
      idsInProgress: computed,
      usedAIModelKind: computed
    });
  }

  /** Модель, сохраненная на беке */
  get usedAIModelKind(): AIModelKind {
    return this.aiModelKind.value;
  }

  get usedAIModelKindPrice(): number | null {
    return this.tariffication.getDisplayPrice(this.usedAIModelKind);
  }

  get allDocs(): AIAggregatedDocModel<
    AIAggregatedDocType.pdf | AIAggregatedDocType.site
  >[] {
    return [...this.siteDocs.items, ...this.pdfDocs.items];
  }

  get idsInProgress(): string[] {
    return this.allDocs
      .filter((doc) => doc.id.value !== null && doc.inProgress)
      .map((doc) => doc.id.value) as string[];
  }

  get docsWithDateError(): boolean {
    return this.allDocs.some((doc) => doc.errorDateCreated);
  }

  async update(): Promise<BaseResponse> {
    const data = this.toJson();

    const response = await this.rootStore.networkStore.api(
      apiUrls.AI_BOTS_UPDATE,
      {
        method: 'POST',
        data
      }
    );

    if (!response.isError) {
      this.rootStore.analyticsStore.sendEvent(
        AnalyticsEvent.smartbotAIBotUpdate
      );
      this.aiModelKind.changeValue(data.model_type);
      this.target.changeValue(data.target);
      this.savedName.changeValue(data.name);
    }

    return response;
  }

  async updateModelKind(modelKind: AIModelKind): Promise<BaseResponse> {
    const response = await this.rootStore.networkStore.api(
      apiUrls.AI_BOTS_UPDATE,
      {
        method: 'POST',
        data: {
          _id: this.id.value,
          model_type: modelKind,
          target: this.target.value
        }
      }
    );

    if (!response.isError) {
      this.aiModelKind.changeValue(modelKind);
    }

    return response;
  }

  async uploadPdfDocs(files: File[]): Promise<void> {
    await Promise.all(
      files.map(async (file) => {
        const doc = AIAggregatedPdfDocModel.fromDefaultParams({
          file,
          rootStore: this.rootStore,
          botId: this.id.value
        });
        this.pdfDocs.addEntity({ key: doc.renderId, entity: doc, start: true });
        const response = await doc.upload(file);
        if (!response.isError) {
          return;
        }
      })
    );

    this.startUpdatingDocsStatus();
  }

  indexSite = async (url: string): Promise<BaseResponse> => {
    const domain = getDomainNameFromUrl(url) || url;

    const site = this.siteDocs.items.find((doc) => doc.id.value === domain);

    const model =
      site ||
      AIAggregatedSiteDocModel.fromDefaultParams({
        url,
        rootStore: this.rootStore,
        botId: this.id.value
      });
    // если сайт уже индексировали, он переместится наверх
    if (site) {
      this.siteDocs.removeEntity(model.renderId);
    }

    this.siteDocs.addEntity({
      key: model.renderId,
      entity: model,
      start: true
    });

    const response = await model.upload({
      url
    });

    if (response.isError) {
      return {
        isError: true
      };
    }

    this.startUpdatingDocsStatus();
    return {
      isError: false
    };
  };

  removeSiteDoc = async (renderId: string): Promise<BaseResponse> => {
    const doc = this.siteDocs.getEntity(renderId);

    if (!doc) {
      return {
        isError: true
      };
    }

    // документа не загрузился до конца
    if (doc.id.value === null) {
      this.siteDocs.removeEntity(renderId);

      return {
        isError: false
      };
    }

    const response = await doc.remove();

    if (!response.isError) {
      this.siteDocs.removeEntity(renderId);
    }

    return response;
  };

  removePdfDoc = async (renderId: string): Promise<BaseResponse> => {
    const doc = this.pdfDocs.getEntity(renderId);

    if (!doc) {
      return {
        isError: true
      };
    }

    // документа не загрузился до конца
    if (doc.id.value === null) {
      this.pdfDocs.removeEntity(renderId);

      return {
        isError: false
      };
    }

    const response = await doc.remove();

    if (!response.isError) {
      this.pdfDocs.removeEntity(renderId);
    }

    return response;
  };

  async getAggregatedDocs(rootStore: IRootStore): Promise<BaseResponse> {
    const response = await rootStore.networkStore.api<AIDocsServer>(
      apiUrls.AI_AGGREGATED_DOCS_LIST,
      {
        method: 'GET',
        data: {
          ai_bot_id: this.id.value
        }
      }
    );

    if (response.isError) {
      return { isError: true };
    }

    const pdfDocs: {
      keys: string[];
      entities: Map<string, AIAggregatedPdfDocModel>;
    } = {
      keys: [],
      entities: new Map()
    };
    const siteDocs: {
      keys: string[];
      entities: Map<string, AIAggregatedSiteDocModel>;
    } = {
      keys: [],
      entities: new Map()
    };

    response.data.docs.forEach((doc) => {
      if (doc.type === AIAggregatedDocType.pdf) {
        const model = AIAggregatedPdfDocModel.fromJson({
          raw: doc,
          botId: this.id.value,
          rootStore
        });
        pdfDocs.keys.push(model.renderId);
        pdfDocs.entities.set(model.renderId, model);
      } else {
        const model = AIAggregatedSiteDocModel.fromJson({
          raw: doc,
          botId: this.id.value,
          rootStore
        });
        siteDocs.keys.push(model.renderId);
        siteDocs.entities.set(model.renderId, model);
      }
    });

    this.pdfDocs.addEntities({
      entities: pdfDocs.entities,
      keys: pdfDocs.keys,
      initial: true
    });

    this.siteDocs.addEntities({
      entities: siteDocs.entities,
      keys: siteDocs.keys,
      initial: true
    });

    this.startUpdatingDocsStatus();

    return {
      isError: false
    };
  }

  updateDocsStatus = async (): Promise<BaseResponse> => {
    const response = await this.rootStore.networkStore.api<AIDocsServer>(
      apiUrls.AI_AGGREGATED_DOCS_POLL,
      {
        method: 'POST',
        data: {
          ai_bot_id: this.id.value,
          agg_keys: this.idsInProgress
        }
      }
    );

    if (response.isError) {
      return {
        isError: true
      };
    }
    //для того, чтобы не обходить несколько раз кладем в Map
    const pdfDocsMap: Map<string, AIDocServer> = new Map();
    const siteDocsMap: Map<string, AIDocServer> = new Map();
    response.data.docs.forEach((rawDoc) => {
      if (rawDoc.type === AIAggregatedDocType.pdf) {
        pdfDocsMap.set(rawDoc.id, rawDoc);
      } else {
        siteDocsMap.set(rawDoc.id, rawDoc);
      }
    });

    if (pdfDocsMap.size) {
      this.pdfDocs.items.forEach((model) => {
        if (model.id.value === null) {
          return;
        }
        const doc = pdfDocsMap.get(model.id.value);
        if (!doc) {
          return;
        }
        model.errors.changeValue(doc.errors || null);
        model.progress.changeValue(doc.progress);
        model.url.changeValue(doc.url);
        model.cost.changeValue(doc.cost);
      });
    }
    if (siteDocsMap.size) {
      this.siteDocs.items.forEach((model) => {
        if (model.id.value === null) {
          return;
        }
        const doc = siteDocsMap.get(model.id.value);
        if (!doc) {
          return;
        }
        model.errors.changeValue(doc.errors || null);
        model.progress.changeValue(doc.progress);
        model.url.changeValue(doc.url);
        model.cost.changeValue(doc.cost);
      });
    }

    return { isError: false };
  };

  startUpdatingDocsStatus = async (): Promise<BaseResponse> => {
    return new Promise((resolve) => {
      this.clearTimer();
      if (this.idsInProgress.length === 0) {
        resolve({ isError: false });
        return;
      }
      const request = async () => {
        if (this.idsInProgress.length === 0) {
          this.clearTimer();
          resolve({ isError: false });
        } else {
          this.updateDocsStatus();
        }
      };
      void request();
      this.timerId = setInterval(request, DOCS_STATUS_POLL_INTERVAL_MS);
    });
  };

  clearTimer = () => {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  };

  static async load(
    rootStore: IRootStore,
    botId: string,
    withExtraInfo = true
  ): Promise<BaseResponse<AIBotModel>> {
    const response = await rootStore.networkStore.api<{ ai_bot: AIBotServer }>(
      apiUrls.AI_BOTS_GET,
      {
        method: 'GET',
        data: {
          _id: botId,
          with_extra_info: withExtraInfo
        },
        errorsMap: mapAIBotGetErrorToTitle,
        showExpectedError: false
      }
    );

    if (response.isError) {
      return {
        isError: true
      };
    }

    return {
      isError: false,
      data: AIBotModel.fromJson(response.data.ai_bot, rootStore)
    };
  }

  static fromJson(raw: AIBotServer, rootStore: IRootStore) {
    return new AIBotModel({
      params: AIEducationParamsModel.fromJson(raw, rootStore.projectId),
      id: raw._id,
      scenarios: raw.extra?.scenarios?.map(normalizeAIBotScenarioData) ?? [],
      channelIds: normalizeAIBotChannelIds(raw.extra?.channel_ids),
      tariffication: AITarifficationModel.fromJson(raw.tariffication),
      rootStore
    });
  }
}
